import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { ITMS } from "@/constants/testIds";
import { useDepots, fmtDepot } from "@/lib/depots";
import { toast } from "sonner";
import { Receipt, CurrencyInr, Handshake as HandshakeIcon, Bus } from "@phosphor-icons/react";
import LeakageDetector from "@/components/LeakageDetector";

export default function PrivateOperators() {
  const depotMap = useDepots();
  const [tab, setTab] = useState("operators");
  const [operators, setOperators] = useState([]);
  const [buses, setBuses] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [form, setForm] = useState({ operator_id: "", period: "2026-02", gps_km: 6800, minimum_km: 6000, rate: 35 });

  const load = async () => {
    const [o, b, i] = await Promise.all([
      api.get("/operators"), api.get("/fleet/buses"), api.get("/operators/invoices"),
    ]);
    setOperators(o.data); setBuses(b.data.filter(x => x.is_private)); setInvoices(i.data);
    if (!form.operator_id && o.data.length) setForm(f => ({ ...f, operator_id: o.data[0].id }));
  };

  useEffect(() => { load(); }, []);

  const genInvoice = async () => {
    try {
      const { data } = await api.post("/operators/payment", form);
      setInvoices((p) => [data, ...p]);
      toast.success(`Invoice ${data.invoice_no} generated · Net ₹${data.net_payable.toLocaleString("en-IN")}`);
    } catch { toast.error("Failed to generate invoice"); }
  };

  return (
    <div className="p-4 lg:p-6 space-y-5 lg:space-y-6">
      <div>
        <div className="label-cap text-slate-500">Contracts & Settlements</div>
        <h1 className="font-display text-2xl lg:text-3xl font-bold leading-tight">Private Operator Management</h1>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiSmall label="Operators" value={operators.length} icon={HandshakeIcon} color="#1E3A8A"/>
        <KpiSmall label="Contract Buses" value={buses.length} icon={Bus} color="#22C55E"/>
        <KpiSmall label="Pending Invoices" value={invoices.filter(i => i.status === "Pending").length} icon={Receipt} color="#F97316"/>
        <KpiSmall label="Net Payable (MTD)" value={"₹" + (invoices.reduce((s,i)=>s+i.net_payable,0)/100000).toFixed(1) + "L"} icon={CurrencyInr} color="#8B5CF6" raw/>
      </div>

      <div data-testid={ITMS.operatorsTabs} className="ktcl-card">
        <div className="flex border-b border-slate-200 overflow-x-auto scroll-thin">
          {[
            { k: "operators", l: "Operators" },
            { k: "buses", l: "Contract Buses" },
            { k: "payments", l: "Payments & Invoices" },
            { k: "engine", l: "Payment Engine" },
            { k: "leakage", l: "Leakage Detector" },
          ].map((t) => (
            <button key={t.k} onClick={() => setTab(t.k)}
              className={`shrink-0 px-4 sm:px-5 py-3 text-sm transition-colors whitespace-nowrap ${tab===t.k?"border-b-2 border-[#1E3A8A] text-[#1E3A8A] font-semibold":"text-slate-500 hover:text-slate-700"}`}>
              {t.l}
            </button>
          ))}
        </div>

        {tab === "operators" && (
          <div className="overflow-x-auto scroll-thin">
            <table className="w-full text-sm min-w-[1000px]">
              <thead className="bg-slate-50">
                <tr className="text-left text-xs text-slate-500">
                  {"Operator ID,Name,Contact,GST,PAN,Bank,Rate (₹/km),Min KM,Excess Rate,Status".split(",").map(h => (
                    <th key={h} className="px-3 py-2 font-medium">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {operators.map((o) => (
                  <tr key={o.id} className="border-t border-slate-100 hover:bg-slate-50">
                    <td className="px-3 py-2 font-mono font-bold text-[#1E3A8A]">{o.operator_id}</td>
                    <td className="px-3 py-2">{o.name}</td>
                    <td className="px-3 py-2 text-xs">{o.contact}</td>
                    <td className="px-3 py-2 font-mono text-xs">{o.gst}</td>
                    <td className="px-3 py-2 font-mono text-xs">{o.pan}</td>
                    <td className="px-3 py-2 font-mono text-xs">{o.bank_account}</td>
                    <td className="px-3 py-2 text-right tabular-nums">₹{o.km_rate}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{o.min_guarantee_km}</td>
                    <td className="px-3 py-2 text-right tabular-nums">₹{o.excess_km_rate}</td>
                    <td className="px-3 py-2"><span className="text-xs px-2 py-0.5 bg-green-50 text-green-700 rounded">{o.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === "buses" && (
          <div className="overflow-x-auto scroll-thin">
            <table className="w-full text-sm min-w-[720px]">
              <thead className="bg-slate-50"><tr className="text-left text-xs text-slate-500">
                {"Fleet ID,Registration,Type,Depot,Battery,Status".split(",").map(h => (<th key={h} className="px-3 py-2 font-medium">{h}</th>))}
              </tr></thead>
              <tbody>
                {buses.map(b => (
                  <tr key={b.id} className="border-t border-slate-100">
                    <td className="px-3 py-2 font-mono font-bold text-[#1E3A8A]">{b.fleet_id}</td>
                    <td className="px-3 py-2 font-mono text-xs">{b.registration_number}</td>
                    <td className="px-3 py-2">{b.bus_type}</td>
                    <td className="px-3 py-2">{fmtDepot(b.depot, depotMap)}</td>
                    <td className="px-3 py-2">{b.battery_health}%</td>
                    <td className="px-3 py-2">{b.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === "payments" && (
          <div className="overflow-x-auto scroll-thin">
            <table className="w-full text-sm min-w-[1100px]">
              <thead className="bg-slate-50"><tr className="text-left text-xs text-slate-500">
                {"Invoice No,Operator,Period,GPS KM,Min KM,Excess,Rate,Gross,Tax,Net Payable,Status".split(",").map(h => (
                  <th key={h} className="px-3 py-2 font-medium">{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {invoices.length === 0 && (
                  <tr><td colSpan={11} className="px-3 py-8 text-center text-slate-500">No invoices yet — use the Payment Engine tab to generate one.</td></tr>
                )}
                {invoices.map((i) => (
                  <tr key={i.id} className="border-t border-slate-100">
                    <td className="px-3 py-2 font-mono font-bold text-[#1E3A8A]">{i.invoice_no}</td>
                    <td className="px-3 py-2">{i.operator_name}</td>
                    <td className="px-3 py-2">{i.period}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{i.gps_km}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{i.minimum_km}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{i.excess_km}</td>
                    <td className="px-3 py-2 text-right tabular-nums">₹{i.rate}</td>
                    <td className="px-3 py-2 text-right tabular-nums">₹{i.gross.toLocaleString("en-IN")}</td>
                    <td className="px-3 py-2 text-right tabular-nums">₹{i.tax.toLocaleString("en-IN")}</td>
                    <td className="px-3 py-2 text-right tabular-nums font-semibold">₹{i.net_payable.toLocaleString("en-IN")}</td>
                    <td className="px-3 py-2"><span className="text-xs px-2 py-0.5 bg-amber-50 text-amber-700 rounded">{i.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === "engine" && (
          <div className="p-6 max-w-3xl space-y-4">
            <div className="label-cap text-slate-500">GPS-verified KM Settlement</div>
            <h3 className="font-display text-xl font-bold">Generate Operator Invoice</h3>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Operator">
                <select className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-md text-sm outline-none"
                  value={form.operator_id} onChange={(e)=>setForm({...form, operator_id: e.target.value})}>
                  {operators.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
                </select>
              </Field>
              <Field label="Period (YYYY-MM)"><Input value={form.period} onChange={(v)=>setForm({...form, period:v})}/></Field>
              <Field label="GPS Verified KM"><Input type="number" value={form.gps_km} onChange={(v)=>setForm({...form, gps_km:+v})}/></Field>
              <Field label="Minimum Guarantee KM"><Input type="number" value={form.minimum_km} onChange={(v)=>setForm({...form, minimum_km:+v})}/></Field>
              <Field label="Rate (₹/km)"><Input type="number" value={form.rate} onChange={(v)=>setForm({...form, rate:+v})}/></Field>
            </div>
            <div className="grid grid-cols-3 gap-4 pt-4 border-t border-slate-100">
              <Stat label="Billable KM" value={Math.max(form.gps_km, form.minimum_km)}/>
              <Stat label="Gross" value={"₹" + (Math.max(form.gps_km, form.minimum_km) * form.rate).toLocaleString("en-IN")}/>
              <Stat label="With 5% Tax" value={"₹" + (Math.max(form.gps_km, form.minimum_km) * form.rate * 1.05).toLocaleString("en-IN")}/>
            </div>
            <button data-testid={ITMS.generateInvoice}
              onClick={genInvoice}
              className="ktcl-btn-primary rounded-md px-5 py-2.5 text-sm font-semibold">
              Generate Invoice
            </button>
          </div>
        )}

        {tab === "leakage" && (
          <div className="p-4">
            <LeakageDetector />
          </div>
        )}
      </div>
    </div>
  );
}

function KpiSmall({ label, value, icon: Icon, color, raw }) {
  return (
    <div className="ktcl-card ktcl-card-hover p-4">
      <div className="flex items-center justify-between">
        <div className="label-cap text-slate-500">{label}</div>
        <Icon size={18} weight="duotone" style={{ color }}/>
      </div>
      <div className="font-display text-2xl font-bold mt-2" style={{ color }}>{raw ? value : value}</div>
    </div>
  );
}
const Field = ({ label, children }) => (<div><div className="text-xs font-medium text-slate-600 mb-1">{label}</div>{children}</div>);
const Input = ({ value, onChange, type = "text" }) => (
  <input type={type} value={value} onChange={(e)=>onChange(e.target.value)}
         className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-md text-sm outline-none focus:border-[#1E3A8A]"/>
);
const Stat = ({ label, value }) => (
  <div className="bg-slate-50 border border-slate-200 rounded-md p-3">
    <div className="label-cap text-slate-500">{label}</div>
    <div className="font-display text-xl font-bold text-[#1E3A8A]">{value}</div>
  </div>
);
